// Lightbox singleton

import wait from '~/utils/animation/wait';

/**
 * Lightbox singleton
 */
class Lightbox
{
	/**
	 * Lightbox element identifier
	 */
	public static LIGHTBOX_ID: string = 'lightbox';
	/**
	 * Class added to lightbox when it's open
	 */
	public static CLASS_OPEN: string = 'open';
	/**
	 * Class added to lightbox when it's open
	 */
	public static CLASS_BODY_LIGHTBOX_FULLSCREEN: string = 'lightbox-mode-fullscreen';
	/**
	 * Duration of hiding previous content
	 */
	private static readonly CONTENT_HIDE_DURATION = 300;
	/**
	 * Duration of showing new content
	 */
	private static readonly CONTENT_SHOW_DURATION = 300;
	/**
	 * Duration of hiding previous lightbox
	 */
	private static LIGHTBOX_HIDE_DURATION = 300;
	/**
	 * Duration of showing new lightbox
	 */
	private static LIGHTBOX_SHOW_DURATION = 300;
	/**
	 * Lightbox instance
	 */
	private static _instance: Lightbox;
	/**
	 * Lightbox in full screen mode?
	 */
	private static fullScreenMode: boolean = false;
	
	/**
	 * Main lightbox element
	 */
	private element: HTMLElement;
	/**
	 * Lightbox content body
	 */
	private body: HTMLElement;
	/**
	 * Origin parent node of content elements
	 */
	private contentOrigin: Node | null;
	/**
	 * List of current content elements
	 */
	private contentElements: ArrayLike<Node>;
	/**
	 * Is lightbox opened?
	 */
	private opened: boolean;
	/**
	 * Function to call when lightbox closed.
	 */
	private closeCallback: (() => void) | undefined;
	/**
	 * Initial touch Y value to prevent body scroll on mobile devices.
	 */
	private initialTouchY: number;
	/**
	 * Window scroll position before open lightbox
	 */
	private windowScroll: [number, number];
	
	/**
	 * Get lightbox instance
	 */
	public static get instance(): Lightbox
	{
		if ( !Lightbox._instance )
		{
			Lightbox._instance = new Lightbox();
		}
		
		return Lightbox._instance;
	}
	
	/**
	 * Toggle Lightbox full screen mode
	 */
	public static toggleFullScreenMode( enabled: boolean ): void
	{
		Lightbox.fullScreenMode = enabled;
	}
	
	/**
	 * Calculate scroll bar width value
	 * 
	 * @returns Scroll bar width.
	 */
	private static calcScrollWidth(): number
	{
		return (
			window.innerWidth - document.body.clientWidth
		);
	}
	
	/**
	 * Remove scroll bar from document
	 * 
	 * Removes vertical scroll bar and sets right padding with scroll bar size.
	 */
	private static removeDocumentScrollBar(): void
	{
		const rootStyle = document.documentElement.style;
		const scrollWidth = Lightbox.calcScrollWidth();
		
		rootStyle.overflowY = 'hidden';
		rootStyle.paddingRight = scrollWidth + 'px';
		document.body.style.overflowY = 'hidden';
	}
	
	/**
	 * Restore scroll bar on document
	 */
	private static restoreDocumentScrollBar(): void
	{
		const rootStyle = document.documentElement.style;
		
		rootStyle.overflowY = '';
		rootStyle.paddingRight = '';
		document.body.style.overflowY = '';
	}
	
	/**
	 * Lightbox singleton
	 */
	private constructor()
	{
		if ( Lightbox._instance )
		{
			return Lightbox._instance;
		}
		
		this.createElements();
		
		this.contentOrigin = null;
		this.contentElements = [];
		this.opened = this.element.classList.contains( Lightbox.CLASS_OPEN );
		this.initialTouchY = -1;
	}
	
	/**
	 * Open lightbox window
	 * 
	 * @param elements Lightbox content elements.
	 * @param closeCallback Function to call when lightbox closed (or opened
	 *  another lightbox).
	 */
	public async open(
		elements: ArrayLike<Node>,
		closeCallback?: () => void,
	): Promise<void>
	{
		await this.placeContentElements( elements );
		this.addToDocument();
		Lightbox.removeDocumentScrollBar();
		this.setOpenState( true );
		this.addDocumentListeners();
		
		this.closeCallback = closeCallback;
		
		if ( Lightbox.fullScreenMode )
		{
			await wait( Lightbox.LIGHTBOX_SHOW_DURATION );
			this.windowScroll = [window.scrollX, window.scrollY];
			document.body.classList.add( Lightbox.CLASS_BODY_LIGHTBOX_FULLSCREEN );
			Lightbox.restoreDocumentScrollBar();
		}
	}
	
	/**
	 * Close lightbox window
	 */
	public async close(): Promise<void>
	{
		this.setOpenState( false );
		this.removeDocumentListeners();
		
		if ( Lightbox.fullScreenMode )
		{
			Lightbox.removeDocumentScrollBar();
			document.body.classList.remove( Lightbox.CLASS_BODY_LIGHTBOX_FULLSCREEN );
			window.scrollTo( ...this.windowScroll );
		}
		
		await wait( Lightbox.LIGHTBOX_HIDE_DURATION );
		Lightbox.restoreDocumentScrollBar();
		this.removeFromDocument();
		await this.removeContentElements();
	}
	
	/**
	 * Create lightbox DOM elements
	 */
	private createElements(): void
	{
		const element = document.createElement( 'aside' );
		element.id = Lightbox.LIGHTBOX_ID;
		
		const content = document.createElement( 'div' );
		content.className = 'content';
		element.appendChild( content );
		
		const header = document.createElement( 'header' );
		content.appendChild( header );
		
		const btnClose = document.createElement( 'button' );
		btnClose.type = 'button';
		btnClose.className = 'close';
		btnClose.textContent = 'Закрыть';
		header.appendChild( btnClose );
		
		const body = document.createElement( 'div' );
		body.className = 'body';
		content.appendChild( body );
		
		this.element = element;
		this.body = body;
		
		btnClose.addEventListener(
			'click',
			() => this.close(),
		);
		
		element.addEventListener(
			'click',
			( event: Event ) =>
			{
				const target = event.target as HTMLElement;
				
				if ( target === element )
				{
					this.close();
				}
			},
		);
		element.addEventListener( 'touchstart', this.onLightboxTouchStart );
		element.addEventListener( 'touchmove', this.onLightboxTouchMove );
	}
	
	/**
	 * Cleanup lightbox content body
	 */
	private async removeContentElements(): Promise<void>
	{
		if ( this.contentElements.length === 0 )
		{
			return;
		}
		
		const bodyStyle = this.body.style;
		
		if ( this.opened )
		{
			bodyStyle.opacity = '1';
			bodyStyle.transition = `opacity ${Lightbox.CONTENT_HIDE_DURATION}ms`;
			
			await wait( 0 );
			
			bodyStyle.opacity = '0';
			
			await wait( Lightbox.CONTENT_HIDE_DURATION );
		}
		
		const withOrigin = ( this.contentOrigin != null );
		const fragment = document.createDocumentFragment();
		
		for ( let i = 0, n = this.contentElements.length; i < n; i++ )
		{
			const element = this.contentElements[i];
			
			if ( withOrigin )
			{
				fragment.appendChild( element );
			}
			else
			{
				this.body.removeChild( element );
			}
		}
		
		if ( this.contentOrigin != null )
		{
			this.contentOrigin.appendChild( fragment );
		}
		
		if ( this.opened )
		{
			bodyStyle.opacity = '';
			bodyStyle.transition = '';
		}
		
		if ( this.closeCallback )
		{
			this.closeCallback();
			this.closeCallback = undefined;
		}
	}
	
	/**
	 * Place elements to lightbox content body
	 */
	private async placeContentElements( elements: ArrayLike<Node> ): Promise<void>
	{
		await this.removeContentElements();
		
		this.contentElements = elements;
		
		if ( elements.length === 0 )
		{
			this.contentOrigin = null;
			
			return;
		}
		
		this.contentOrigin = elements[0].parentNode;
		
		const bodyStyle = this.body.style;
		
		if ( this.opened )
		{
			bodyStyle.opacity = '0';
			bodyStyle.transition = `opacity ${Lightbox.CONTENT_SHOW_DURATION}ms`;
			
			await wait( 0 );
		}
		
		const fragment = document.createDocumentFragment();
		
		for ( let i = 0, n = this.contentElements.length; i < n; i++ )
		{
			const element = this.contentElements[i];
			
			fragment.appendChild( element );
		}
		
		this.body.appendChild( fragment );
		
		if ( this.opened )
		{
			bodyStyle.opacity = '1';
			
			await wait( Lightbox.CONTENT_SHOW_DURATION );
			
			bodyStyle.opacity = '';
			bodyStyle.transition = '';
		}
	}
	
	/**
	 * Set the open state of lightbox
	 * 
	 * @param state Is lightbox open?
	 */
	private setOpenState( state: boolean ): void
	{
		if ( state === this.opened )
		{
			return;
		}
		
		this.opened = state;
		
		if ( state )
		{
			this.element.classList.add( Lightbox.CLASS_OPEN );
		}
		else
		{
			this.element.classList.remove( Lightbox.CLASS_OPEN );
		}
	}
	
	/**
	 * Add lightbox DOM elements to document
	 */
	private addToDocument(): void
	{
		if ( this.element.parentElement )
		{
			return;
		}
		
		const docBody = document.body;
		
		docBody.insertBefore( this.element, docBody.firstChild );
	}
	
	/**
	 * Remove lightbox DOM elements from document
	 */
	private removeFromDocument(): void
	{
		if ( !this.element.parentElement )
		{
			return;
		}
		
		const docBody = document.body;
		
		docBody.removeChild( this.element );
	}
	
	/**
	 * Add listeners to Document when Lightbox open
	 */
	private addDocumentListeners(): void
	{
		document.addEventListener( 'keyup', this.onDocumentKeyUp );
	}
	
	/**
	 * Remove listeners from Document when Lightbox close
	 */
	private removeDocumentListeners(): void
	{
		document.removeEventListener( 'keyup', this.onDocumentKeyUp );
	}
	
	/**
	 * Listener for keystrokes on Document
	 */
	private onDocumentKeyUp = ( event: KeyboardEvent ): void =>
	{
		if ( event.keyCode === 27 /* Escape */ )
		{
			this.close();
		}
	}
	
	/**
	 * Handle touch start to save initial Y position.
	 */
	private onLightboxTouchStart = ( event: TouchEvent ): void =>
	{
		if ( event.targetTouches.length === 1 )
		{
			this.initialTouchY = event.targetTouches[0].clientY;
		}
	}
	
	/**
	 * Handle touch move to prevent body scroll on mobile devices.
	 */
	private onLightboxTouchMove = ( event: TouchEvent ): void =>
	{
		if (
			Lightbox.fullScreenMode
			|| ( event.targetTouches.length !== 1 )
		)
		{
			return;
		}
		
		const distance = event.targetTouches[0].clientY - this.initialTouchY;
		
		if (
			( distance > 0 )
			&& ( this.element.scrollTop === 0 )
		)
		{
			event.preventDefault();
			
			return;
		}
		
		if (
			( distance < 0 )
			&& (
				(this.element.scrollHeight - this.element.scrollTop)
				<= this.element.clientHeight
			)
		)
		{
			event.preventDefault();
		}
	}
}

/**
 * Module
 */
export {
	Lightbox as default,
};



// WEBPACK FOOTER //
// ./app/elements/Lightbox.ts